"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.COMPANY_PROFILE_MERGE_PREFIX = exports.CompanyProfileInputSchema = exports.CompanyProfileSchema = exports.CompanyProfileFieldsSchema = exports.CompanySignatorySchema = exports.StoredSignatureSchema = exports.MAX_SIGNATURE_PNG_BASE64 = exports.CompanyAddressSchema = exports.COMPANY_ENTITY_TYPE_LABELS = exports.COMPANY_ENTITY_TYPES = void 0;
exports.resolveSignatory = resolveSignatory;
exports.canAutoSign = canAutoSign;
exports.formatCompanyAddress = formatCompanyAddress;
exports.companyMergeFields = companyMergeFields;
const zod_1 = require("zod");
const company_1 = require("./mergeProviders/company");
/**
 * THE COMPANY'S OWN IDENTITY — one platform row, read by every render.
 *
 * Before this existed, our legal name and address were typed by hand into
 * merge data per request, and the `from` address on outbound mail was a
 * literal in each email-service template. Two sources for one fact drift, and
 * the drift lands in a signed contract.
 *
 * SNAPSHOT RULE: a binder copies the projection below at freeze time. Changing
 * this row must never rewrite what somebody already signed.
 */
exports.COMPANY_ENTITY_TYPES = [
    'single_member_llc', 'multi_member_llc', 'corporation', 's_corporation', 'sole_proprietorship',
];
/** Human-readable labels for `entityType`, so every consumer (contracts,
 * merge fields, UI) prints "Single-Member LLC" instead of the raw enum
 * token `single_member_llc`. */
exports.COMPANY_ENTITY_TYPE_LABELS = {
    single_member_llc: 'Single-Member LLC',
    multi_member_llc: 'Multi-Member LLC',
    corporation: 'Corporation',
    s_corporation: 'S Corporation',
    sole_proprietorship: 'Sole Proprietorship',
};
/** A US two-letter state/territory code. Uppercase, exactly two letters. */
const StateCode = zod_1.z.string().regex(/^[A-Z]{2}$/, 'must be a two-letter state code, e.g. VA');
const nonBlank = (label) => zod_1.z.string().trim().min(1, `${label} must not be blank`);
exports.CompanyAddressSchema = zod_1.z.object({
    line1: nonBlank('address line 1'),
    line2: zod_1.z.string().trim().min(1).optional(),
    city: nonBlank('city'),
    state: StateCode,
    postalCode: nonBlank('postal code'),
    country: zod_1.z.string().trim().min(2).default('US'),
});
/**
 * A STORED SIGNATURE — our side of an agreement, captured once.
 *
 * `typed` is a name rendered in a script face; `drawn` is a PNG of an actual
 * mark. They are LEGALLY EQUIVALENT (see SignaturePad): the mark evidences
 * intent, and the session, timestamps and presented hash evidence who. Storing
 * ours means an agreement can reach `executed` the moment the other party
 * signs, instead of waiting for somebody here to open a countersign screen.
 *
 * The PNG is base64 and lives on the profile row, so it is CAPPED: a DynamoDB
 * item is 400KB and a signature that pushed the row over would make the whole
 * company profile unsaveable — a far worse failure than a rejected upload.
 * 96KB of base64 is roughly a 70KB PNG, which is a generous signature.
 */
exports.MAX_SIGNATURE_PNG_BASE64 = 96 * 1024;
exports.StoredSignatureSchema = zod_1.z.object({
    kind: zod_1.z.enum(['typed', 'drawn']),
    /** The name as it should be rendered in script, when `kind` is `typed`. */
    typedName: zod_1.z.string().trim().min(1).optional(),
    /** Base64 PNG WITHOUT the data-URL prefix, when `kind` is `drawn`. */
    imagePngBase64: zod_1.z.string().max(exports.MAX_SIGNATURE_PNG_BASE64, 'signature image is too large — draw a smaller mark or use a typed signature').optional(),
    capturedAt: zod_1.z.string().min(1),
}).refine((s) => (s.kind === 'typed' ? !!s.typedName : !!s.imagePngBase64), {
    message: 'a typed signature needs a name and a drawn one needs an image — a signature with '
        + 'neither would print as nothing on an executed agreement',
    path: ['kind'],
});
/**
 * SOMEBODY WHO MAY SIGN FOR US.
 *
 * Normally the CEO, but a sales rep can be assigned per agreement — which is
 * the whole reason this is a LIST with a default rather than one name on the
 * profile. `signature` is optional on purpose: an authorised signatory with no
 * stored mark is still assignable, and their agreements simply wait for a
 * manual countersign instead of auto-executing. Refusing to store them would
 * mean you could not name a signatory until they had sat down at a canvas.
 */
exports.CompanySignatorySchema = zod_1.z.object({
    signatoryId: zod_1.z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
    name: nonBlank('signatory name'),
    /** "Chief Executive Officer" — printed under the signature line. */
    title: nonBlank('signatory title'),
    email: zod_1.z.string().email().optional(),
    signature: exports.StoredSignatureSchema.optional(),
    updatedAt: zod_1.z.string().min(1),
});
/**
 * The FIELDS, un-refined.
 *
 * Exported because `.refine()` returns a ZodEffects, which has no `.omit()`
 * or `.shape` — and the admin form needs both to validate one field at a time
 * and to drop the two server-owned ones. Adding the cross-field rules below
 * silently broke that page's client-side validation, so the object it needs is
 * now a first-class export rather than something callers reach into.
 *
 * PARSE AGAINST `CompanyProfileSchema`, never this: this one does not enforce
 * the default-signatory rules.
 */
exports.CompanyProfileFieldsSchema = zod_1.z.object({
    legalEntityName: nonBlank('legal entity name'),
    /** The trading name, when it differs. Omitted rather than blank. */
    dba: zod_1.z.string().trim().min(1).optional(),
    entityType: zod_1.z.enum(exports.COMPANY_ENTITY_TYPES),
    stateOfFormation: StateCode,
    ein: zod_1.z.string().trim().min(1).optional(),
    registeredAddress: exports.CompanyAddressSchema,
    /** Omitted means "same as registered" — see `companyMergeFields`. */
    mailingAddress: exports.CompanyAddressSchema.optional(),
    phone: nonBlank('phone'),
    website: zod_1.z.string().url().optional(),
    supportEmail: zod_1.z.string().email(),
    billingEmail: zod_1.z.string().email(),
    accountsEmail: zod_1.z.string().email(),
    salesEmail: zod_1.z.string().email(),
    adminEmail: zod_1.z.string().email(),
    /** Who gets internal notifications. May be empty; never contains a blank. */
    notificationEmails: zod_1.z.array(zod_1.z.string().email()).default([]),
    /** Drives the governing-law and venue clauses in every agreement. */
    governingLawState: StateCode,
    venueCounty: nonBlank('venue county'),
    /**
     * WHO SIGNS FOR US. Additive and optional: a profile written before this
     * existed has none, and its agreements countersign by hand exactly as they
     * always did.
     */
    signatories: zod_1.z.array(exports.CompanySignatorySchema).max(20).default([]),
    /**
     * The one used when an agreement does not name another. Held as an ID rather
     * than an `isDefault` flag on each row, because a flag permits two defaults
     * and there is no truthful answer to which of them signs.
     */
    defaultSignatoryId: zod_1.z.string().optional(),
    updatedAt: zod_1.z.string().min(1),
    updatedBy: zod_1.z.string().min(1),
});
/**
 * The cross-field signatory rules.
 *
 * Written out on BOTH schemas rather than through a generic helper: a helper
 * that takes `ZodTypeAny` erases the object's type, and every consumer of
 * `CompanyProfile` then degrades to `any` — which is how a typo in
 * `companyMergeFields` would stop being a compile error. Two short copies of a
 * rule the tests pin beats one copy that costs the whole file its types.
 */
const DUPLICATE_IDS = {
    message: 'two signatories share an id',
    path: ['signatories'],
};
const DEFAULT_MUST_EXIST = {
    message: 'the default signatory is not in the list — an agreement would resolve to nobody '
        + 'and silently wait for a countersign that was never assigned',
    path: ['defaultSignatoryId'],
};
const idsUnique = (p) => new Set(p.signatories.map((s) => s.signatoryId)).size === p.signatories.length;
const defaultExists = (p) => !p.defaultSignatoryId || p.signatories.some((s) => s.signatoryId === p.defaultSignatoryId);
exports.CompanyProfileSchema = exports.CompanyProfileFieldsSchema
    .refine(idsUnique, { message: DUPLICATE_IDS.message, path: [...DUPLICATE_IDS.path] })
    .refine(defaultExists, { message: DEFAULT_MUST_EXIST.message, path: [...DEFAULT_MUST_EXIST.path] });
/**
 * What an admin form submits: every field except the two the server owns,
 * carrying the SAME cross-field rules, so the form disables Save for exactly
 * the reasons the server would reject the write.
 */
exports.CompanyProfileInputSchema = exports.CompanyProfileFieldsSchema
    .omit({ updatedAt: true, updatedBy: true })
    .refine(idsUnique, { message: DUPLICATE_IDS.message, path: [...DUPLICATE_IDS.path] })
    .refine(defaultExists, { message: DEFAULT_MUST_EXIST.message, path: [...DEFAULT_MUST_EXIST.path] });
exports.COMPANY_PROFILE_MERGE_PREFIX = 'company_';
/**
 * WHO SIGNS THIS ONE.
 *
 * `signatoryId` wins when it names a real signatory; otherwise the profile's
 * default. Returns `undefined` when neither resolves, and the caller must treat
 * that as "countersign by hand" — never as "sign as somebody else". An
 * agreement executed under the wrong name is worse than one still waiting.
 *
 * A NAMED-BUT-MISSING id deliberately does NOT fall back to the default: it
 * means the agreement asked for a specific person who is no longer authorised,
 * and quietly substituting the CEO would put the wrong signature on a contract.
 */
function resolveSignatory(profile, signatoryId) {
    const list = profile.signatories ?? [];
    if (signatoryId)
        return list.find((s) => s.signatoryId === signatoryId);
    if (!profile.defaultSignatoryId)
        return undefined;
    return list.find((s) => s.signatoryId === profile.defaultSignatoryId);
}
/** Whether this signatory can sign WITHOUT a human opening the countersign screen. */
function canAutoSign(s) {
    return !!s?.signature;
}
/** "100 Main St, Suite 2, Norfolk, VA 23510" — the form a contract prints. */
function formatCompanyAddress(a) {
    const parts = [a.line1, a.line2, a.city].filter((p) => !!p && p.trim().length > 0);
    return `${parts.join(', ')}, ${a.state} ${a.postalCode}`;
}
/**
 * Project the profile into flat, ALREADY-STRINGIFIED merge values.
 *
 * Blank and absent values are OMITTED rather than emitted as "". The renderer
 * throws on a blank merge field on purpose (`documents-render/src/merge.ts`),
 * and an omitted key produces that useful failure at render time, whereas an
 * empty string would sail through and print nothing where our legal name
 * belongs.
 */
function companyMergeFields(profile, 
/** Which signatory this particular agreement is being signed by. */
signatoryId) {
    const signatory = resolveSignatory(profile, signatoryId);
    /**
     * Every key is listed here, so adding one to COMPANY_KEYS without producing
     * it fails to compile. `undefined` is legitimate (an optional EIN); it is
     * omitted below, exactly as before.
     */
    const values = {
        legal_name: profile.legalEntityName,
        dba: profile.dba,
        display_name: profile.dba ?? profile.legalEntityName,
        entity_type: exports.COMPANY_ENTITY_TYPE_LABELS[profile.entityType],
        state_of_formation: profile.stateOfFormation,
        ein: profile.ein,
        address: formatCompanyAddress(profile.registeredAddress),
        mailing_address: formatCompanyAddress(profile.mailingAddress ?? profile.registeredAddress),
        city: profile.registeredAddress.city,
        state: profile.registeredAddress.state,
        postal_code: profile.registeredAddress.postalCode,
        phone: profile.phone,
        website: profile.website,
        support_email: profile.supportEmail,
        billing_email: profile.billingEmail,
        accounts_email: profile.accountsEmail,
        sales_email: profile.salesEmail,
        admin_email: profile.adminEmail,
        governing_law_state: profile.governingLawState,
        venue_county: profile.venueCounty,
        // Absent when no signatory resolves. Omitted rather than blank, so a
        // document that prints our signatory fails loudly instead of showing an
        // empty line above a signature.
        signatory_name: signatory?.name,
        signatory_title: signatory?.title,
    };
    const out = {};
    for (const key of company_1.COMPANY_KEYS) {
        const v = values[key];
        if (typeof v === 'string' && v.trim().length > 0) {
            out[`${exports.COMPANY_PROFILE_MERGE_PREFIX}${key}`] = v.trim();
        }
    }
    return out;
}
//# sourceMappingURL=companyProfile.js.map